import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Project, ProjectStatus, ProjectSimulation } from '../../types/project';
import { Client } from '../../types/client';
import { clientService } from '../../services/clientService';

function addMonths(date: Date, months: number): Date {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
}

interface ProjectDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (project: Project) => void;
    project?: Project;
}

interface ProjectFormData extends Omit<Project, 'id' | 'statusLabel' | 'simulation' | 'simulations'> {
    simulation?: ProjectSimulation;
}

const ProjectDialog: React.FC<ProjectDialogProps> = ({
    open,
    onClose,
    onSave,
    project,
}) => {
    const { t } = useTranslation();
    const [clients, setClients] = useState<Client[]>([]);
    const [formData, setFormData] = useState<ProjectFormData>({
        name: '',
        description: '',
        status: ProjectStatus.OPPORTUNITY,
        clientId: 0,
        companyId: 0,
        managerId: 0,
        simulation: undefined,
        secoes: [], // Adicionado
    });

    useEffect(() => {
        loadClients();
    }, []);

    const [touched, setTouched] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTouched(false);
        if (project) {
            setFormData({
                ...project,
                description: project.description || '',
                simulation: project.simulations?.length ? project.simulations[0] : undefined
            });
        } else {
            // Novo projeto: sempre começa limpo (antes herdava os dados do último projeto editado)
            setFormData({
                name: '', description: '', status: ProjectStatus.OPPORTUNITY, clientId: 0,
                companyId: 0, managerId: 0, simulation: undefined, secoes: [],
            });
        }
    }, [project, open]);

    const nameError = touched && !formData.name?.trim();
    const clientError = touched && !Number(formData.clientId);

    // Transições permitidas manualmente; as demais seguem o fluxo simulação → proposta → aceite
    const statusOptions: ProjectStatus[] = project
        ? Array.from(new Set([
            project.status,
            ...(project.status === ProjectStatus.ACTIVE ? [ProjectStatus.COMPLETED] : []),
            ...(project.status !== ProjectStatus.COMPLETED ? [ProjectStatus.CANCELLED] : []),
        ]))
        : [ProjectStatus.OPPORTUNITY];

    const loadClients = async () => {
        try {
            const data = await clientService.getClients();
            setClients(data);
        } catch (error) {
            console.error('Erro ao carregar clientes:', error);
        }
    };

    const handleTextChange = (field: keyof ProjectFormData) => (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleSelectChange = (field: keyof ProjectFormData) => (
        event: React.ChangeEvent<{ value: unknown }> | SelectChangeEvent
    ) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleSubmit = () => {
        setTouched(true);
        if (!formData.name?.trim() || !Number(formData.clientId)) return;
        const projectData: Project = {
            id: project?.id || 0,
            name: formData.name,
            description: formData.description,
            status: formData.status,
            statusLabel: '', // ou o valor correto
            clientId: Number(formData.clientId),
            companyId: formData.companyId,
            managerId: formData.managerId,
            simulation: formData.simulation,
            simulations: [],
            secoes: formData.secoes ?? [], // Adicionado
        };
        onSave(projectData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {project ? t('manager.projects.edit') : t('manager.projects.new')}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label={t('manager.projects.fields.name')}
                            value={formData.name || ''}
                            onChange={handleTextChange('name')}
                            error={nameError}
                            helperText={nameError ? 'Informe o nome do projeto' : undefined}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={t('manager.projects.fields.description')}
                            value={formData.description || ''}
                            onChange={handleTextChange('description')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth disabled={statusOptions.length === 1}>
                            <InputLabel>{t('manager.projects.fields.status')}</InputLabel>
                            <Select
                                value={formData.status}
                                onChange={handleSelectChange('status')}
                                label={t('manager.projects.fields.status')}
                            >
                                {statusOptions.map((status) => (
                                    <MenuItem key={status} value={status}>{t(`manager.projects.status.${status}`)}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                {project ? 'Proposta e execução avançam pelo fluxo de simulação e aceite' : 'Todo projeto começa como oportunidade'}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required error={clientError}>
                            <InputLabel>{t('manager.projects.fields.client')}</InputLabel>
                            <Select
                                value={Number(formData.clientId) ? String(formData.clientId) : ''}
                                onChange={handleSelectChange('clientId')}
                                label={t('manager.projects.fields.client')}
                            >
                                {clients.map((client) => (
                                    <MenuItem key={client.id} value={String(client.id)}>
                                        {client.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {clientError && <FormHelperText>Selecione o cliente</FormHelperText>}
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProjectDialog; 